/**
 * Modern onay modalı — uygulama içi kritik işlemler için kullanılır.
 *
 * Kullanım:
 *   const [confirmOpts, setConfirmOpts] = useState<ConfirmOptions | null>(null)
 *
 *   // Bir butona bağlamak için:
 *   setConfirmOpts({
 *     title: 'Konteyner silinsin mi?',
 *     description: 'Bu işlem geri alınamaz.',
 *     detail: 'docker rm -f my-container',
 *     variant: 'danger',
 *     onConfirm: async () => { await api.delete(...) },
 *   })
 *
 *   // JSX'te:
 *   {confirmOpts && <ConfirmModal {...confirmOpts} onCancel={() => setConfirmOpts(null)} />}
 */
import { createContext, useContext, useState, useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, AlertOctagon, Info, CheckCircle2, XCircle, Loader2 } from 'lucide-react'
import { confirmationInputError } from '@/lib/confirmationInput'
import api from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { applyOperationReasonPolicy, operationReasonValue } from '@/lib/operationReasonPolicy'
import { verifyBiometricForAction } from '@/lib/biometricPreferences'

export type ConfirmVariant = 'danger' | 'warning' | 'info'

export interface ConfirmOptions {
  title: string
  description?: string
  children?: ReactNode
  /** Tek satır monospace preview — yapılacak işlemi gösterir */
  detail?: string
  variant?: ConfirmVariant
  confirmLabel?: string
  cancelLabel?: string
  input?: {
    label: string
    placeholder?: string
    value?: string
    required?: boolean
    minLength?: number
    matchValue?: string
    validationMessage?: string
  }
  onConfirm: (value?: string) => Promise<void> | void
  onCancel: () => void
}

export type ConfirmRequest = Omit<ConfirmOptions, 'onConfirm' | 'onCancel'>
type ConfirmContextValue = {
  confirm: (options: ConfirmRequest) => Promise<boolean>
  prompt: (options: ConfirmRequest) => Promise<string | null>
}
const ConfirmContext = createContext<ConfirmContextValue | null>(null)

export function useConfirmDialog() {
  const value = useContext(ConfirmContext)
  if (!value) throw new Error('ConfirmProvider is missing')
  return value
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth()
  type PendingRequest = {
    options: ConfirmRequest
    resolve: (value: boolean | string | null) => void
    prompt: boolean
    optionalReason: boolean
    settled?: boolean
  }
  const [request, setRequest] = useState<PendingRequest | null>(null)
  const requestRef = useRef<PendingRequest | null>(null)
  const queueRef = useRef<PendingRequest[]>([])
  const [reasonRequired, setReasonRequired] = useState(false)

  useEffect(() => {
    if (!token) {
      setReasonRequired(false)
      return
    }
    let active = true
    void api.get('/workspaces/operation-policy').then(response => {
      if (active) setReasonRequired(response.data?.require_operation_reason === true)
    }).catch(() => { /* Varsayılan politika: işlem nedeni isteğe bağlı. */ })
    const update = (event: Event) => setReasonRequired((event as CustomEvent).detail?.require_operation_reason === true)
    window.addEventListener('servernox-operation-policy-changed', update)
    return () => { active = false; window.removeEventListener('servernox-operation-policy-changed', update) }
  }, [token])

  function open(options: ConfirmRequest, prompt: boolean) {
    return new Promise<boolean | string | null>(resolve => {
      const normalized = applyOperationReasonPolicy(options, reasonRequired)
      const pending = { options: normalized.options, resolve, prompt, optionalReason: normalized.optionalReason }
      if (requestRef.current) {
        queueRef.current.push(pending)
      } else {
        requestRef.current = pending
        setRequest(pending)
      }
    })
  }

  function finish(expected: PendingRequest, result: boolean | string | null, advance = true) {
    const current = requestRef.current
    // ConfirmModal başarı durumunu kısa süre gösterip eski onCancel callback'ini
    // daha sonra çağırabilir. O sırada kuyruktaki bir sonraki onay açılmışsa
    // eski modal kesinlikle yeni isteği kapatmamalı.
    if (!current || current !== expected) return
    if (!current.settled) {
      current.settled = true
      current.resolve(result)
    }
    if (!advance) return
    const next = queueRef.current.shift() || null
    requestRef.current = next
    setRequest(next)
  }

  const value: ConfirmContextValue = {
    confirm: async options => (await open(options, false)) === true,
    prompt: async options => {
      const result = await open(options, true)
      return typeof result === 'string' ? result : null
    },
  }

  // Context-backed dialogs only collect the user's approval. The actual
  // operation starts after this promise resolves, so close this layer instead
  // of showing ConfirmModal's generic operation-success state.
  return <ConfirmContext.Provider value={value}>
    {children}
    {request && <ConfirmModal
      {...request.options}
      onConfirm={inputValue => finish(request, request.prompt ? operationReasonValue(inputValue, request.optionalReason) : true)}
      onCancel={() => finish(request, request.prompt ? null : false)}
    />}
  </ConfirmContext.Provider>
}

const VARIANTS = {
  danger: {
    icon: AlertOctagon,
    iconClass: 'text-red-400',
    bgClass: 'bg-red-500/10 border-red-500/30',
    btnClass: 'bg-red-600 hover:bg-red-500 focus:ring-red-500/50',
    detailClass: 'bg-red-950/40 border-red-800/30 text-red-200',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-yellow-400',
    bgClass: 'bg-yellow-500/10 border-yellow-500/30',
    btnClass: 'bg-yellow-600 hover:bg-yellow-500 focus:ring-yellow-500/50',
    detailClass: 'bg-yellow-950/40 border-yellow-800/30 text-yellow-200',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-400',
    bgClass: 'bg-blue-500/10 border-blue-500/30',
    btnClass: 'bg-panel-accent text-panel-on-accent hover:bg-panel-accent hover:brightness-95 focus:ring-panel-accent/50',
    detailClass: 'bg-blue-950/40 border-blue-800/30 text-blue-200',
  },
}

let bodyScrollLocks = 0
let bodyOverflowBeforeModal = ''

function lockBodyScroll() {
  if (bodyScrollLocks === 0) {
    bodyOverflowBeforeModal = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }
  bodyScrollLocks++
  return () => {
    bodyScrollLocks = Math.max(0, bodyScrollLocks - 1)
    if (bodyScrollLocks === 0) document.body.style.overflow = bodyOverflowBeforeModal
  }
}

export default function ConfirmModal({
  title,
  description,
  children,
  detail,
  variant = 'warning',
  confirmLabel,
  cancelLabel = 'İptal',
  input,
  onConfirm,
  onCancel,
}: ConfirmOptions) {
  const [state, setState] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [inputValue, setInputValue] = useState(input?.value || '')
  const cancelBtnRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const descriptionId = useId()
  const v = VARIANTS[variant]
  const Icon = v.icon

  // Auto-focus cancel (safer default)
  useEffect(() => {
    cancelBtnRef.current?.focus()
  }, [])

  // Modal açıkken arka planın özellikle mobilde kaymasını önle.
  useEffect(() => {
    return lockBodyScroll()
  }, [])

  // Escape key closes
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state === 'idle') onCancel()
      if (e.key !== 'Tab' || !dialogRef.current) return
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [state, onCancel])

  const handleConfirm = async () => {
    if (input) {
      const validationError = confirmationInputError(inputValue, input)
      if (validationError) {
        setErrorMsg(validationError)
        setState('error')
        return
      }
    }
    if (variant === 'danger' || variant === 'warning') {
      const bio = await verifyBiometricForAction(title, description)
      if (!bio.success) {
        if (bio.cancelled) return
        setErrorMsg(bio.message || 'Biyometrik doğrulama başarısız oldu')
        setState('error')
        return
      }
    }
    setState('loading')
    setErrorMsg('')
    try {
      await onConfirm(input ? inputValue : undefined)
      setState('ok')
      setTimeout(onCancel, 800)
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail || e?.message || 'Bilinmeyen hata')
      setState('error')
    }
  }

  const defaultLabel = variant === 'danger' ? 'Sil' : variant === 'warning' ? 'Onayla' : 'Tamam'

  const modal = (
    <div
      className="confirm-modal-layer fixed inset-0 z-[10000] isolate flex items-start sm:items-center justify-center overflow-y-auto overscroll-contain"
      style={{
        height: 'var(--app-visual-height, 100dvh)',
        backdropFilter: 'blur(4px)',
        backgroundColor: 'rgba(0,0,0,0.65)',
      }}
      onClick={e => { if (e.target === e.currentTarget && state === 'idle') onCancel() }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className="w-full max-w-sm bg-panel-card border border-panel-border rounded-2xl shadow-2xl overflow-y-auto overscroll-contain"
        style={{ maxHeight: 'calc(var(--app-visual-height, 100dvh) - var(--confirm-modal-top-space) - var(--confirm-modal-bottom-space))', animation: 'modalIn 0.15s ease-out' }}
      >
        {/* Header */}
        <div className={`flex items-start gap-3 px-5 pt-5 pb-4 border-b border-panel-border ${v.bgClass} border-0`}>
          <div className={`mt-0.5 shrink-0 ${v.iconClass}`}>
            <Icon size={22} />
          </div>
          <div className="min-w-0">
            <h3 id={titleId} className="font-semibold text-sm text-panel-text leading-snug">{title}</h3>
            {description && (
              <p id={descriptionId} className="text-xs text-panel-subtle mt-1 leading-relaxed">{description}</p>
            )}
          </div>
        </div>

        {/* Body */}
        {(detail || children || input || errorMsg || state === 'ok') && (
          <div className="px-5 py-4 space-y-3">
            {children && state !== 'ok' && <div>{children}</div>}
            {detail && state !== 'ok' && (
              <div className={`text-xs font-mono rounded-lg px-3 py-2 border ${v.detailClass} leading-relaxed break-all`}>
                <span className="text-panel-muted select-none">$ </span>{detail}
              </div>
            )}
            {input && state !== 'ok' && (
              <label className="block text-xs text-panel-subtle">
                {input.label}
                <input
                  autoFocus
                  value={inputValue}
                  onChange={e => {
                    setInputValue(e.target.value)
                    if (state === 'error') {
                      setState('idle')
                      setErrorMsg('')
                    }
                  }}
                  placeholder={input.placeholder}
                  required={input.required}
                  minLength={input.minLength}
                  aria-invalid={state === 'error' && !!errorMsg}
                  className={`mt-1 w-full min-h-11 rounded-xl border bg-panel-bg px-3 text-panel-text outline-none focus:border-panel-accent ${state === 'error' && errorMsg ? 'border-red-500/60' : 'border-panel-border'}`}
                />
                {!!input.minLength && <span className="mt-1 block text-[10px] text-panel-muted">En az {input.minLength} karakter · {inputValue.trim().length}/{input.minLength}</span>}
              </label>
            )}
            {state === 'ok' && (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle2 size={16} /> Başarıyla tamamlandı
              </div>
            )}
            {state === 'error' && errorMsg && (
              <div className="flex items-start gap-2 text-red-400 text-xs bg-red-950/40 border border-red-800/30 rounded-lg px-3 py-2">
                <XCircle size={14} className="shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 justify-end px-5 pb-5 pt-2">
          {state !== 'ok' && (
            <button
              ref={cancelBtnRef}
              onClick={onCancel}
              disabled={state === 'loading'}
              className="px-4 py-2 text-sm rounded-xl bg-panel-border hover:bg-panel-hover disabled:opacity-50 transition-colors"
            >
              {cancelLabel}
            </button>
          )}
          {state !== 'ok' && (
            <button
              onClick={handleConfirm}
              disabled={state === 'loading'}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-xl text-white font-medium transition-all focus:outline-none focus:ring-2 disabled:opacity-50 ${v.btnClass}`}
            >
              {state === 'loading' ? (
                <><Loader2 size={14} className="animate-spin" /> İşleniyor...</>
              ) : state === 'error' ? (
                <>Tekrar dene</>
              ) : (
                confirmLabel ?? defaultLabel
              )}
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.95) translateY(-8px); }
          to   { opacity: 1; transform: scale(1)    translateY(0); }
        }
      `}</style>
    </div>
  )
  return typeof document === 'undefined' ? modal : createPortal(modal, document.body)
}
